import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createFarmerProfileByHtx } from '../../../../store';
import type { FarmerAttachmentInput } from '../../../../api/ekyc';
import { uploadFile, uploadFailureMessage } from '../../../../api/file';
import { showToast } from '../../../../components/base/Toast';
import { confirmModal } from '../../../../components/base/ConfirmModal';
import { CropSelect } from '../../../../components/feature/CropSelect';
import { ExtractionResult } from '../../../../components/feature/ExtractionResult';
import type { ApplyExtracted } from '../../../../components/feature/DocumentUploadSlot';
import { extractDocument, fieldNumber, fieldString, type ExtractionDocumentType, type ExtractionJob } from '../../../../api/aiExtraction';
import { shrinkDocumentImage } from '../../../../utils/imageResize';
import { matchCatalogName } from '../../../../utils/catalogMatch';
import { useCrops } from '../../../../hooks/useCrops';
import { ClearableInput } from '../../../../components/base/ClearableInput';
import { address as addressCheck, cccd12, fullName as nameCheck, phoneVn, positive, validate as check } from '../../../../utils/validators';

type SlotStatus = 'idle' | 'uploading' | 'uploaded' | 'error';

interface SlotState {
  status: SlotStatus;
  fileId?: string;
  error?: string;
}

interface Preview {
  url: string;
  name: string;
  isImage: boolean;
}

const SLOTS: Array<{
  key: 'cccd' | 'landTitle' | 'contract';
  label: string;
  purpose: 'FARMER_IDENTITY_DOCUMENT' | 'LAND_TITLE_DOCUMENT' | 'CONTRACT_DOCUMENT';
  attachmentType: FarmerAttachmentInput['type'];
  docType: ExtractionDocumentType;
}> = [
  { key: 'cccd', label: 'CCCD/CMND (mặt trước — AI tự điền họ tên, số CCCD, địa chỉ)', purpose: 'FARMER_IDENTITY_DOCUMENT', attachmentType: 'CCCD_FRONT', docType: 'CCCD_FRONT' },
  { key: 'landTitle', label: 'Giấy chứng nhận thửa đất', purpose: 'LAND_TITLE_DOCUMENT', attachmentType: 'LAND_TITLE', docType: 'LAND_TITLE' },
  { key: 'contract', label: 'Hợp đồng bao tiêu', purpose: 'CONTRACT_DOCUMENT', attachmentType: 'OFFTAKE_CONTRACT', docType: 'OFFTAKE_CONTRACT' },
];

/** One attachment slot. The picked file is shown straight away from a local
 * object URL while the upload runs behind it. Once stored, AI reads it in the
 * background and fills the form's still-empty fields; neither the upload nor
 * the reading ever disables the form's own fields, and only the upload holds
 * up "Tạo hồ sơ". */
function DocumentSlot({
  slot,
  registerAttachment,
  onBusyChange,
  onExtracted,
}: {
  slot: (typeof SLOTS)[number];
  registerAttachment: (a: FarmerAttachmentInput | null) => void;
  onBusyChange: (busy: boolean) => void;
  onExtracted: ApplyExtracted;
}) {
  const [state, setState] = useState<SlotState>({ status: 'idle' });
  const [preview, setPreview] = useState<Preview | null>(null);
  const [reading, setReading] = useState(false);
  const [job, setJob] = useState<ExtractionJob | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // The upload outlives a user closing this modal mid-flight — without this
  // guard, its final setState would fire against an unmounted component.
  const mountedRef = useRef(true);
  // Set back to true on (re)mount: StrictMode mounts, unmounts and remounts
  // every component in dev, so a cleanup-only effect left this false for good
  // and every finished upload was thrown away with the slot stuck on
  // "Đang tải lên...".
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Object URLs hold the file in memory until revoked.
  useEffect(() => {
    if (!preview) return;
    return () => URL.revokeObjectURL(preview.url);
  }, [preview]);

  // Submitting while the bytes are in flight would create the profile without
  // this document.
  const busy = state.status === 'uploading';
  useEffect(() => {
    onBusyChange(busy);
  }, [busy, onBusyChange]);

  // Bumped on every pick/remove so a slower, superseded upload can't land on
  // top of the file the user chose after it.
  const pickSeq = useRef(0);

  async function handleFile(file: File) {
    const seq = ++pickSeq.current;
    // A re-pick replaces the earlier document; until the new one lands, the old
    // one must not be submitted in its place.
    registerAttachment(null);
    setPreview({ url: URL.createObjectURL(file), name: file.name, isImage: file.type.startsWith('image/') });
    setState({ status: 'uploading' });
    setJob(null);
    setReading(false);
    let fileId: string;
    try {
      const body = await shrinkDocumentImage(file);
      fileId = await uploadFile(body, slot.purpose, body.type || file.type || 'application/octet-stream');
      if (!mountedRef.current || seq !== pickSeq.current) return;
      registerAttachment({ fileId, type: slot.attachmentType });
      setState({ status: 'uploaded', fileId });
    } catch (err) {
      if (!mountedRef.current || seq !== pickSeq.current) return;
      setState({ status: 'error', error: uploadFailureMessage(err, slot.label) });
      return;
    }
    setReading(true);
    const result = await extractDocument(fileId, slot.docType);
    if (!mountedRef.current || seq !== pickSeq.current) return;
    setReading(false);
    setJob(result);
    if (result.status === 'DONE' && result.fields) onExtracted(result.fields, { overwrite: false });
  }

  function remove() {
    pickSeq.current++;
    registerAttachment(null);
    setPreview(null);
    setState({ status: 'idle' });
    setJob(null);
    setReading(false);
  }

  return (
    <div className={`rounded-xl border bg-white p-3 ${state.status === 'error' ? 'border-red-300' : 'border-slate-200'}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold text-slate-700">{slot.label}</p>
        {state.status === 'idle' && <span className="text-[10px] text-slate-400">Chưa đính kèm</span>}
        {state.status === 'uploading' && <span className="text-[10px] text-sky-600 animate-pulse">Đang tải lên...</span>}
        {state.status === 'uploaded' && <span className="text-[10px] text-agrida-600">✓ Đã đính kèm</span>}
        {state.status === 'error' && <span className="text-[10px] text-red-600">Tải lên lỗi</span>}
      </div>

      {preview && (
        <div className="relative mt-2 overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
          <a href={preview.url} target="_blank" rel="noopener noreferrer" className="block" title="Xem ảnh lớn">
            {preview.isImage ? (
              <img src={preview.url} alt={slot.label} className={`h-40 w-full object-contain ${state.status === 'uploading' ? 'opacity-60' : ''}`} />
            ) : (
              <div className="flex h-16 items-center gap-2 px-3">
                <i className="ri-file-pdf-2-line text-2xl text-red-500" />
                <span className="truncate text-xs text-slate-700">{preview.name}</span>
              </div>
            )}
          </a>
          {state.status !== 'uploading' && (
            <button
              type="button"
              onClick={remove}
              aria-label={`Bỏ ${slot.label}`}
              className="absolute right-1.5 top-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-slate-600 shadow"
            >
              <i className="ri-close-line text-base" />
            </button>
          )}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          // Cleared so picking the same file again after an error still fires onChange.
          e.target.value = '';
          if (file) void handleFile(file);
        }}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={state.status === 'uploading'}
        className="w-full mt-2 h-8 rounded-lg border border-dashed border-agrida-300 bg-agrida-50 text-xs font-semibold text-agrida-700 disabled:opacity-60"
      >
        {preview ? 'Chọn lại tệp khác' : 'Chọn ảnh / tệp'}
      </button>

      {state.status === 'error' && (
        <p className="mt-1.5 text-[11px] text-red-600">
          {state.error} Bạn vẫn có thể tạo hồ sơ và bổ sung giấy tờ sau.
        </p>
      )}

      <ExtractionResult
        docType={slot.docType}
        reading={reading}
        job={job}
        onOverwrite={job?.fields ? () => onExtracted(job.fields!, { overwrite: true }) : undefined}
      />
    </div>
  );
}

type FieldKey = 'fullName' | 'cccd' | 'phone' | 'address' | 'areaHa';
type FormValues = Record<'fullName' | 'cccd' | 'phone' | 'address' | 'areaHa', string>;

/** Họ tên, CCCD, SĐT and Địa chỉ are required. Phone follows the register
 * page's rule — the 10-digit local form a farmer later signs up with, so a
 * profile keyed on anything else would never be matched to that account. */
function validate(values: FormValues): Partial<Record<FieldKey, string>> {
  return {
    fullName: check(values.fullName, [nameCheck], true, 'họ tên nông hộ'),
    cccd: check(values.cccd, [cccd12], true, 'số CCCD'),
    phone: check(values.phone, [phoneVn], true, 'số điện thoại'),
    address: check(values.address, [addressCheck], true, 'địa chỉ'),
    areaHa: check(values.areaHa, [positive('Diện tích', 10_000)]),
  };
}

function Required() {
  return <span className="text-red-500"> *</span>;
}

export function CreateFarmerModal({ htxId, onClose, onCreated }: { htxId?: string; onClose: () => void; onCreated: () => void }) {
  const [fullName, setFullName] = useState('');
  const [cccd, setCccd] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [areaHa, setAreaHa] = useState('');
  const [mainCrop, setMainCrop] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Errors follow the values: one AI filled in wrong shows red at once; an
  // empty required field only after "Tạo hồ sơ" was pressed.
  const [submitted, setSubmitted] = useState(false);
  const values: FormValues = { fullName, cccd, phone, address, areaHa };
  const allErrors = validate(values);
  const errors = Object.fromEntries(
    (Object.keys(allErrors) as FieldKey[]).map((k) => [k, values[k].trim() || submitted ? allErrors[k] : undefined]),
  ) as Partial<Record<FieldKey, string>>;
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [busySlots, setBusySlots] = useState<ReadonlySet<string>>(new Set());
  const attachmentsRef = useRef<Map<string, FarmerAttachmentInput>>(new Map());
  const uploading = busySlots.size > 0;
  const crops = useCrops(htxId);

  // What AI read off each document fills only the fields still empty — never
  // something the HTX already typed — unless it asks to overwrite.
  const extractedHandlers = useMemo<Record<(typeof SLOTS)[number]['key'], ApplyExtracted>>(() => {
    // Only what AI actually read is filled; a field it could not read stays empty.
    const fill = (overwrite: boolean, next: string | undefined, set: (fn: (prev: string) => string) => void) => {
      if (next === undefined || next === '') return;
      set((prev) => (overwrite || !prev.trim() ? next : prev));
    };
    return {
      cccd: (f, { overwrite }) => {
        fill(overwrite, fieldString(f.fullName), setFullName);
        const id = fieldString(f.cccd);
        // The form takes a 12-digit CCCD only; an old 9-digit CMND is left for the HTX.
        if (id && /^[0-9]{12}$/.test(id)) fill(overwrite, id, setCccd);
        fill(overwrite, fieldString(f.address), setAddress);
      },
      landTitle: (f, { overwrite }) => {
        fill(overwrite, fieldNumber(f.areaHa)?.toString(), setAreaHa);
      },
      contract: (f, { overwrite }) => {
        fill(overwrite, matchCatalogName(fieldString(f.mainCrop), (crops ?? []).map((c) => c.name)), setMainCrop);
        fill(overwrite, fieldNumber(f.areaHa)?.toString(), setAreaHa);
      },
    };
  }, [crops]);

  const setSlotBusy = useCallback((key: string, busy: boolean) => {
    setBusySlots((prev) => {
      if (prev.has(key) === busy) return prev;
      const next = new Set(prev);
      if (busy) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);
  const busyHandlers = useMemo(
    () => Object.fromEntries(SLOTS.map((slot) => [slot.key, (busy: boolean) => setSlotBusy(slot.key, busy)])) as Record<string, (busy: boolean) => void>,
    [setSlotBusy],
  );

  async function submit() {
    // The button used to be disabled until a name was typed, with nothing on
    // screen saying why, and a server error only flashed by as a toast — testers
    // read both as "creating is broken". Every problem now stays on the form, in
    // red, next to the field it belongs to.
    setSubmitted(true);
    if (Object.values(allErrors).some(Boolean)) {
      setSubmitError('Vui lòng sửa các ô được đánh dấu đỏ.');
      return;
    }
    if (uploading) {
      setSubmitError('Đang tải giấy tờ lên — vui lòng chờ xong rồi bấm tạo lại.');
      return;
    }
    setSubmitError(null);
    setSubmitting(true);
    try {
      const result = await createFarmerProfileByHtx(
        {
          fullName: fullName.trim(),
          cccd: cccd.trim() || undefined,
          phone: phone.trim() || undefined,
          address: address.trim() || undefined,
          areaHa: areaHa ? Number(areaHa) : undefined,
          mainCrop: mainCrop.trim() || undefined,
          attachments: Array.from(attachmentsRef.current.values()),
        },
        htxId,
      );
      showToast('Đã tạo hồ sơ nông hộ', 'success');
      if (result.warning) {
        // Shown before onCreated() closes this modal — a toast alone can't
        // carry this much text legibly, and this component won't exist any
        // more once the modal closes.
        await confirmModal({ title: 'Có thể trùng hồ sơ', message: result.warning, confirmText: 'Đã hiểu', cancelText: 'Đóng', variant: 'primary' });
      }
      onCreated();
    } catch (e) {
      setSubmitError(e instanceof Error && e.message ? e.message : 'Tạo hồ sơ thất bại, vui lòng thử lại.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[97] flex items-end sm:items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl bg-white p-4 space-y-3 max-h-[100dvh] sm:max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-gray-900">Tạo hồ sơ nông hộ mới</h3>
          <button type="button" onClick={onClose} className="text-gray-400"><i className="ri-close-line text-lg" /></button>
        </div>
        <p className="text-[11px] text-slate-500 -mt-1">
          Nông hộ chưa cần có tài khoản — HTX nhập thông tin và đính kèm giấy tờ trước, nông hộ tự đăng ký sau sẽ được gán vào hồ sơ này.
          Ô có dấu <span className="text-red-500">*</span> là bắt buộc.
        </p>

        <div className="grid grid-cols-2 gap-2">
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Họ tên nông hộ<Required /></label>
            <ClearableInput value={fullName} onValueChange={(v) => { setFullName(v); setSubmitError(null); }} error={errors.fullName} placeholder="Nguyễn Văn A" aria-label="họ tên" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Số CCCD<Required /></label>
            <ClearableInput
              value={cccd}
              inputMode="numeric"
              maxLength={12}
              onValueChange={(v) => { setCccd(v.replace(/\D/g, '').slice(0, 12)); setSubmitError(null); }}
              error={errors.cccd}
              placeholder="12 chữ số"
              aria-label="số CCCD"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Số điện thoại<Required /></label>
            <ClearableInput
              value={phone}
              type="tel"
              inputMode="numeric"
              maxLength={10}
              onValueChange={(v) => { setPhone(v.replace(/\D/g, '').slice(0, 10)); setSubmitError(null); }}
              error={errors.phone}
              placeholder="10 chữ số"
              aria-label="số điện thoại"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Địa chỉ<Required /></label>
            <ClearableInput value={address} onValueChange={(v) => { setAddress(v); setSubmitError(null); }} error={errors.address} placeholder="Ấp/xã, huyện, tỉnh" aria-label="địa chỉ" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Diện tích (ha)</label>
            <ClearableInput type="number" min={0} value={areaHa} onValueChange={(v) => { setAreaHa(v); setSubmitError(null); }} error={errors.areaHa} aria-label="diện tích" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Cây trồng chính</label>
            <CropSelect value={mainCrop} htxId={htxId} onChange={(name) => setMainCrop(name)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white" />
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold text-slate-700 mb-2">Đính kèm giấy tờ (tùy chọn — có thể bổ sung sau)</p>
          <div className="space-y-2">
            {SLOTS.map((slot) => (
              <DocumentSlot
                key={slot.key}
                slot={slot}
                onBusyChange={busyHandlers[slot.key]}
                onExtracted={extractedHandlers[slot.key]}
                registerAttachment={(a) => {
                  if (a) attachmentsRef.current.set(slot.key, a);
                  else attachmentsRef.current.delete(slot.key);
                }}
              />
            ))}
          </div>
        </div>

        {submitError && (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {submitError}
          </div>
        )}

        <button type="button" disabled={submitting} onClick={submit} className="w-full rounded-lg bg-agrida-600 text-white text-sm font-semibold py-2.5 disabled:opacity-50">
          {submitting ? 'Đang tạo...' : uploading ? 'Đang tải giấy tờ lên...' : 'Tạo hồ sơ nông hộ'}
        </button>
      </div>
    </div>
  );
}
